import React, { useContext, useMemo, useCallback } from "react";
import Toolbar, { Item } from "devextreme-react/toolbar";
import Button from "devextreme-react/button";
import UserPanel from "../user-panel/user-panel";
import OrganizationSelect from "./organization-select";
import ThemeToggle from "./theme-toggle";
import "./header.scss";
import { Template } from "devextreme-react/core/template";
import { Box, Link, FormControl, Select, MenuItem } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth";
import { useUnsavedWork } from "../../contexts/unsavedWork";
import { useLocationFetch } from "../../hooks/useLocationFetch";
import { Context } from "../../utils/config";
import { ROLE_SYSTEM_ADMIN, ROLE_JDTECH_ADMIN } from "../../utils/constants";
import { colors } from "../../pages";
import { useThemeMode } from "../../contexts/theme-mode";

export default function Header({ menuToggleEnabled, title, toggleMenu }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { guardedNavigate } = useUnsavedWork();
  const { location: globalLocation, setLocation: setGlobalLocation } =
    useContext(Context);
  const { locations: fetchedLocations } = useLocationFetch();
  const { isDark, toggleColorMode } = useThemeMode();

  // Determine if the location selector should be visible.
  // Note: SALON_ADMIN is intentionally excluded from isAdmin. They see the selector
  // via assignedLocations.length >= 2, but don't get "All Locations" (product decision).
  const isAdmin =
    user?.userRoles?.includes(ROLE_SYSTEM_ADMIN) ||
    user?.userRoles?.includes(ROLE_JDTECH_ADMIN);
  const hasMultipleLocations =
    (user?.assignedLocations?.length ?? 0) >= 2 || isAdmin;

  // Build the options list
  const locationOptions = useMemo(() => {
    const options = [];
    // "All Locations" only for admin roles
    if (isAdmin) {
      options.push({ location_id: -1, location_name: "All Locations" });
    }
    // Use fetched locations (admins get all org locations, others get assigned)
    if (fetchedLocations?.length > 0) {
      options.push(...fetchedLocations);
    }
    return options;
  }, [isAdmin, fetchedLocations]);

  const selectedLocationId =
    globalLocation?.location_id ?? user?.location_id ?? -1;

  const handleLocationChange = useCallback(
    (event) => {
      const newId = event.target.value;
      const selected = locationOptions.find((l) => l.location_id === newId);
      if (selected && selected.location_id !== globalLocation?.location_id) {
        try {
          sessionStorage.setItem("selectedLocationId", String(newId));
          sessionStorage.setItem(
            "selectedLocationName",
            selected.location_name || ""
          );
        } catch (e) {
          /* storage unavailable */
        }
        setGlobalLocation(selected);
      }
    },
    [locationOptions, setGlobalLocation, globalLocation]
  );

  return (
    <header className={"header-component"}>
      <Toolbar className={"header-toolbar"}>
        <Item
          visible={menuToggleEnabled}
          location={"before"}
          widget={"dxButton"}
          cssClass={"menu-button"}
        >
          <Button icon="menu" stylingMode="text" onClick={toggleMenu} />
        </Item>
        <Item location={"before"}>
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Link
              component="button"
              type="button"
              aria-label="Back"
              underline="none"
              sx={{
                color: "#434343 !important",
                ml: 3,
                p: 0,
                border: 0,
                background: "none",
                fontFamily: "inherit",
                fontSize: "24px",
                lineHeight: 1,
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
              onClick={(event) => {
                event.preventDefault();
                guardedNavigate(() => navigate(-1));
              }}
            >
              <i
                className="dx-icon dx-icon-arrowleft"
                style={{
                  color: colors.black,
                  background: colors.gold,
                  fontSize: "20px",
                  padding: "0.5rem",
                  borderRadius: "6px",
                }}
              />
            </Link>
            <Link
              component="button"
              type="button"
              underline="none"
              sx={{
                color: isDark
                  ? `${colors.black} !important`
                  : `${colors.white} !important`,
                ml: 1,
                border: 0,
                fontFamily: "inherit",
                cursor: "pointer",
                background: isDark ? colors.white : colors.black,
                borderRadius: "6px",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                px: "0.65rem",
                py: "0.35rem",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
              onClick={(event) => {
                event.preventDefault();
                guardedNavigate(() => navigate("/pulse"));
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/icons/pulse.png`}
                alt=""
                sx={{
                  width: 18,
                  height: 18,
                  display: "block",
                  objectFit: "contain",
                }}
              />
              <span
                style={{
                  fontFamily: "inherit",
                  fontSize: "14px",
                  lineHeight: 1,
                }}
              >
                Pulse
              </span>
            </Link>
          </Box>
        </Item>

        {user?.userRoles?.includes(ROLE_JDTECH_ADMIN) && (
          <Item location={"center"}>
            <OrganizationSelect />
          </Item>
        )}

        {hasMultipleLocations && (
          <Item location={"center"}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                aria-label="Select location"
                value={selectedLocationId}
                onChange={handleLocationChange}
                displayEmpty
                sx={{
                  backgroundColor: "background.paper",
                  borderRadius: "4px",
                  height: "36px",
                  fontSize: "14px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  },
                }}
                startAdornment={
                  <LocationOnIcon
                    sx={{ color: colors.gold, fontSize: 20, mr: 0.5 }}
                  />
                }
              >
                {locationOptions.map((loc) => (
                  <MenuItem key={loc.location_id} value={loc.location_id}>
                    {loc.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Item>
        )}

        <Item
          location={"after"}
          locateInMenu={"auto"}
          menuItemTemplate={"userPanelTemplate"}
        >
          <Button
            className={"user-button authorization"}
            width={210}
            height={"100%"}
            stylingMode={"text"}
          >
            <UserPanel menuMode={"context"} />
          </Button>
        </Item>
        <Item location={"after"} locateInMenu={"never"}>
          <ThemeToggle isDark={isDark} onToggle={toggleColorMode} />
        </Item>
        <Template name={"userPanelTemplate"}>
          <UserPanel menuMode={"list"} />
        </Template>
      </Toolbar>
    </header>
  );
}
